import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import Card from '../../../../components/card/card.component';
import { IScreeningMCBCreditSupport } from '../../../../types/screening.types';
import { useUser } from '../../../../contexts/user.context';

const baseClass = 'smb-development-tab';
const translationKey = 'screening.smb-development';

const colors: Record<string, string> = {
  IP: '#5BC7D9',
  KH: '#2E6CBD',
  SPK: '#118F84',
  TOO: '#92D050',
};

const headers = ['category', 'projects', 'sum'];
const keys = ['subsidizing', 'guarantee', 'grant'];

const SmbDevelopmentTab = () => {
  const { t } = useTranslation();
  const { smbDevelopment } = useKato().screeningInfo;
  const { mcbSubjects, registeredSubjects, totalSum, creditSupport } =
    smbDevelopment || {};
  const { isModulePresent } = useUser();

  const mcbSubjectsData = mcbSubjects
    ? [
        {
          name: t(`${translationKey}.mcb-subjects.total`),
          total: +mcbSubjects.total,
        },
        {
          name: t(`${translationKey}.mcb-subjects.registered`),
          IP: +mcbSubjects.IP,
          KH: +mcbSubjects.KH,
          SPK: +mcbSubjects.SPK,
          TOO: +mcbSubjects.TOO,
          value:
            +mcbSubjects.IP +
            +mcbSubjects.KH +
            +mcbSubjects.SPK +
            +mcbSubjects.TOO,
        },
      ]
    : [];

  const registeredSubjectsData = registeredSubjects
    ? Object.keys(registeredSubjects).map((key) => ({
        name: t(`${translationKey}.registered-subjects.${key}`),
        value: +registeredSubjects[key],
      }))
    : [];

  const creditSupportData = creditSupport
    ? keys.map((key) => ({
        category: t(`${translationKey}.credit-support.${key}`),
        projects:
          creditSupport[
            `${key}_projects` as keyof IScreeningMCBCreditSupport
          ] || 0,
        sum:
          creditSupport[`${key}_sum` as keyof IScreeningMCBCreditSupport] || 0,
      }))
    : [];

  if (!smbDevelopment) return null;

  console.log('mcbSubjectsData ', mcbSubjectsData);

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`${baseClass}__grid`]}>
        {/* Субъекты МСБ */}
        {isModulePresent('mcb-subjects') && (
          <Card title={t(`${translationKey}.mcb-subjects.title`)}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                layout="vertical"
                data={mcbSubjectsData}
                margin={{ top: 10, bottom: 10, left: 60, right: 30 }}
                barCategoryGap={20}
                barGap={-50}
              >
                <CartesianGrid horizontal vertical={false} />
                <XAxis
                  type="number"
                  hide
                  // allowDataOverflow
                  // scale="log"
                  domain={[30, 'dataMax + 1000']}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: '0.625rem', fill: '#818689' }}
                />
                <Tooltip
                  formatter={(value: number, key: string) => [
                    value.toLocaleString('ru-RU'),
                    t(`${translationKey}.mcb-subjects.${key}`),
                  ]}
                  cursor={{ fill: 'transparent' }}
                />

                {/* Total */}
                <Bar dataKey="total" fill="#118F84" radius={[0, 4, 4, 0]}>
                  <LabelList
                    dataKey="total"
                    position="right"
                    formatter={(val) => `${val?.toLocaleString()}`}
                    style={{ fill: '#000', fontWeight: 600 }}
                  />
                </Bar>

                {/* Registered - stacked */}
                {['IP', 'KH', 'SPK', 'TOO'].map((key, _idx, arr) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="registered"
                    fill={colors[key]}
                    radius={
                      key === arr[arr.length - 1] ? [0, 4, 4, 0] : undefined
                    }
                    // minPointSize={(value: any) => (value > 0 ? 100 : 0)}
                  >
                    <LabelList
                      dataKey={key}
                      position="center"
                      formatter={(val) => `${val?.toLocaleString()}`}
                      style={{
                        fill: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Bar>
                ))}

                {/* Total label for stacked registered */}
                <Bar dataKey="value" fill="transparent">
                  <LabelList
                    dataKey="value"
                    position="right"
                    formatter={(val) => `${val?.toLocaleString()}`}
                    style={{ fill: '#000', fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {/* Registered Subjects */}

        {isModulePresent('registered-subjects') && (
          <Card title={t(`${translationKey}.registered-subjects.title`)}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={registeredSubjectsData}
                margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                barCategoryGap={10}
                barGap={10}
              >
                <CartesianGrid horizontal={true} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={60}
                  tick={{
                    fontSize: '0.625rem',
                    fill: 'var(--color-secondary)',
                    width: 60,
                  }}
                />
                <YAxis hide />
                <Tooltip
                  formatter={(val: number) => [`${val.toLocaleString()} ед.`]}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar dataKey="value" fill="#118F84" radius={[4, 4, 0, 0]}>
                  <LabelList
                    dataKey="value"
                    position="insideTop"
                    formatter={(val) => `${val?.toLocaleString()}`}
                    style={{
                      fontWeight: 600,
                      fill: '#fff',
                      fontSize: '0.75rem',
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {/* Total Sum */}
        {isModulePresent('total-sum') && (
          <Card title={t(`${translationKey}.total-sum`)}>
            <div className={classes[`${baseClass}__total-sum`]}>
              <div>
                <span>{totalSum?.toLocaleString() ?? '0'}</span>
                <span>млн тенге</span>
              </div>
            </div>
          </Card>
        )}

        {/* Финансово-кредитная поддержка */}

        {isModulePresent('credit-support') && (
          <Card title={t(`${translationKey}.credit-support.title`)}>
            <div className={classes[`${baseClass}__table`]}>
              <table>
                <thead>
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index}>
                        <span>
                          {t(`${translationKey}.credit-support.${header}`)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {creditSupportData.map((item, index) => (
                    <tr key={index}>
                      {headers.map((key, idx) => (
                        <td key={idx}>
                          <span>
                            {item[key as keyof typeof item]?.toLocaleString(
                              'ru-RU',
                            )}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SmbDevelopmentTab;
